/**
 * SettingsUtils Module
 * 
 * Utility functions for managing construction worker payslip settings.
 * Handles loading, saving, and converting between API and legacy formats.
 * 
 * Features:
 * - API-based settings with localStorage fallback
 * - Legacy format support for backward compatibility
 * - Default settings generation
 * - Field settings management
 * 
 * @module SettingsUtils
 */

import {
  constructionPaymentItems,
  constructionDeductionItems,
  constructionSonotaItems,
  constructionAttendanceItems,
  constructionNotesItems,
  constructionFurikomiItems,
  constructionBonusPaymentItems,
  constructionBonusDeductionItems,
  constructionBonusNotesItems,
  constructionBonusFurikomiItems,
  constructionBonusSonotaItems,
} from './DataConfiguration';
import { CONSTRUCTION_BONUS_SETTINGS_DATA_TYPE } from '../../../../utils/constructionBonusContract';
import { PayslipSettingsService, PayslipSettings as ApiPayslipSettings, FieldSettings } from '../../../../services/PayslipSettingsService';

export type ConstructionSettingsDataType = 'construction' | typeof CONSTRUCTION_BONUS_SETTINGS_DATA_TYPE;

/**
 * Interface defining construction worker payslip settings structure.
 * Contains appearance settings, display toggles, and field visibility controls.
 */
export interface ConstructionPayslipSettings {
  borderColor: string;           // Border color for main table (hex color)
  backgroundColor: string;       // Background color for main table (hex color)
  roumuhiBorderColor: string;    // Border color for roumuhi (labor cost) section (hex color)
  roumuhiBackgroundColor: string; // Background color for roumuhi section (hex color)
  fontSize: string;              // Font size (e.g., '10px')
  fontFamily: string;            // Font family name
  showCompanyName: boolean;      // Whether to show company name
  showEmployeeCode: boolean;     // Whether to show employee code
  showMemo: boolean;             // Whether to show memo section
  furikomi: {                    // Furikomi (transfer) field settings
    enabled: string[];           // Array of enabled field IDs
    lastUpdated: string;         // ISO timestamp of last update
    updatedBy: string;           // User who made the update
  };
  payment: {                     // Payment field settings
    enabled: string[];           // Array of enabled field IDs
    lastUpdated: string;         // ISO timestamp of last update
    updatedBy: string;           // User who made the update
  };
  deduction: {                   // Deduction field settings
    enabled: string[];           // Array of enabled field IDs
    lastUpdated: string;         // ISO timestamp of last update
    updatedBy: string;           // User who made the update
  };
  attendance: {                  // Attendance field settings
    enabled: string[];           // Array of enabled field IDs
    lastUpdated: string;         // ISO timestamp of last update
    updatedBy: string;           // User who made the update
  };
  notes: {                       // Notes field settings (currently unused)
    enabled: string[];           // Array of enabled field IDs
    lastUpdated: string;         // ISO timestamp of last update
    updatedBy: string;           // User who made the update
  };
  sonota: {                      // その他 section (その他支給, その他控除, 年末調整還付金)
    enabled: string[];           // Array of enabled field IDs
    lastUpdated: string;
    updatedBy: string;
  };
}

/**
 * Loads payslip settings from API with localStorage fallback.
 * 
 * Attempts to fetch settings from the backend API first.
 * Falls back to default settings if API call fails or no settings are found.
 * Converts API format to legacy format for compatibility.
 * 
 * @param dataType - Type of settings to load ('construction')
 * @returns Promise resolving to ConstructionPayslipSettings
 * 
 * @example
 * ```ts
 * const settings = await loadSettings('construction');
 * console.log(settings.borderColor); // '#4caf50'
 * ```
 */
export const loadSettings = async (dataType: ConstructionSettingsDataType): Promise<ConstructionPayslipSettings> => {
  if (dataType === CONSTRUCTION_BONUS_SETTINGS_DATA_TYPE) {
    return loadSettingsFromLocalStorage(CONSTRUCTION_BONUS_SETTINGS_DATA_TYPE);
  }

  try {
    const apiSettings = await PayslipSettingsService.getSettingsWithFallback('construction');
    if (apiSettings) {
      return convertApiSettingsToLegacy(apiSettings);
    }
  } catch (error) {
    console.error('Error loading settings from API:', error);
  }

  return getDefaultSettings();
};

/**
 * Converts API settings format to legacy format for backward compatibility.
 * 
 * Maps API settings structure to the legacy ConstructionPayslipSettings format.
 * Provides default values for any missing fields.
 * 
 * @param apiSettings - API format settings from backend
 * @returns Legacy format ConstructionPayslipSettings
 * 
 * @private
 */
const convertApiSettingsToLegacy = (apiSettings: ApiPayslipSettings): ConstructionPayslipSettings => {
  const legacySettings: ConstructionPayslipSettings = {
    // Appearance settings with defaults
    borderColor: apiSettings.borderColor || '#4caf50',                           // Default green border
    backgroundColor: apiSettings.backgroundColor || '#eaffea',                   // Default light green background
    roumuhiBorderColor: apiSettings.roumuhiBorderColor || '#FF8C00',             // Default orange border for roumuhi
    roumuhiBackgroundColor: apiSettings.roumuhiBackgroundColor || '#FFD4A3',     // Default light orange background for roumuhi
    fontSize: apiSettings.fontSize || '10px',                                    // Default font size
    fontFamily: apiSettings.fontFamily || 'Yu Gothic',                          // Default Japanese font
    
    // Display toggles (default to true if undefined)
    showCompanyName: apiSettings.showCompanyName !== undefined ? apiSettings.showCompanyName : true,
    showEmployeeCode: apiSettings.showEmployeeCode !== undefined ? apiSettings.showEmployeeCode : true,
    showMemo: apiSettings.showMemo !== undefined ? apiSettings.showMemo : true,
    
    // Field settings - ensure all sections have proper field settings
    furikomi: ensureFieldSettings(apiSettings.furikomi, constructionFurikomiItems.map(item => item.id)),
    payment: ensureFieldSettings(apiSettings.payment, constructionPaymentItems.map(item => item.id)),
    // Deduction defaults to first 6 items, attendance to first 4 items
    deduction: ensureFieldSettings(apiSettings.deduction, constructionDeductionItems.slice(0, 6).map(item => item.id)),
    attendance: ensureFieldSettings(apiSettings.attendance, constructionAttendanceItems.map(item => item.id)),
    notes: ensureFieldSettings(apiSettings.notes, constructionNotesItems.map(item => item.id)),
    sonota: ensureFieldSettings((apiSettings as { sonota?: FieldSettings }).sonota, constructionSonotaItems.map(item => item.id))
  };
  
  return legacySettings;
};

/**
 * Ensures field settings have all required properties with defaults.
 * 
 * Validates that field settings exist and have enabled array, lastUpdated, and updatedBy.
 * Returns default settings if any are missing.
 * 
 * @param fieldSettings - Optional field settings from API
 * @param defaultEnabled - Default array of enabled field IDs if settings are missing
 * @returns Complete FieldSettings object
 * 
 * @private
 */
/** Appends any missing default field IDs (e.g. new 深夜 / 休日 attendance columns). */
const mergeEnabledFieldIds = (saved: string[] | undefined, defaultEnabled: string[]): string[] => {
  const merged = [...(saved ?? [])];
  for (const id of defaultEnabled) {
    if (!merged.includes(id)) {
      merged.push(id);
    }
  }
  return merged;
};

const ensureFieldSettings = (fieldSettings: FieldSettings | undefined, defaultEnabled: string[]): FieldSettings => {
  // Return default settings if fieldSettings is missing or has no enabled array
  if (!fieldSettings || !fieldSettings.enabled) {
    return {
      enabled: defaultEnabled,
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin'
    };
  }
  
  return {
    enabled: mergeEnabledFieldIds(fieldSettings.enabled, defaultEnabled),
    lastUpdated: fieldSettings.lastUpdated || new Date().toISOString(),
    updatedBy: fieldSettings.updatedBy || 'admin'
  };
};

/**
 * Returns default payslip settings for construction workers.
 * 
 * Provides initial settings with:
 * - Default green color scheme for main table
 * - Default orange color scheme for roumuhi section
 * - Standard font settings
 * - All display options enabled
 * - Default field selections (all items enabled for most sections)
 * 
 * @returns Default ConstructionPayslipSettings object
 * 
 * @example
 * ```ts
 * const defaultSettings = getDefaultSettings();
 * // Returns settings with green borders, light green background, etc.
 * ```
 */
export const getDefaultBonusSettings = (): ConstructionPayslipSettings => {
  return {
    borderColor: '#4caf50',
    backgroundColor: '#eaffea',
    roumuhiBorderColor: '#FF8C00',
    roumuhiBackgroundColor: '#FFD4A3',
    fontSize: '10px',
    fontFamily: 'Yu Gothic',
    showCompanyName: true,
    showEmployeeCode: true,
    showMemo: true,
    furikomi: {
      enabled: constructionBonusFurikomiItems.map((item) => item.id),
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin',
    },
    payment: {
      enabled: constructionBonusPaymentItems.map((item) => item.id),
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin',
    },
    deduction: {
      enabled: constructionBonusDeductionItems.map((item) => item.id),
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin',
    },
    attendance: {
      enabled: [],
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin',
    },
    notes: {
      enabled: constructionBonusNotesItems.map((item) => item.id),
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin',
    },
    sonota: {
      enabled: constructionBonusSonotaItems.map((item) => item.id),
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin',
    },
  };
};

export const getDefaultSettings = (): ConstructionPayslipSettings => {
  return {
    // Default color scheme: green theme for main table
    borderColor: '#4caf50',                    // Green border
    backgroundColor: '#eaffea',                // Light green background
    roumuhiBorderColor: '#FF8C00',             // Orange border for roumuhi section
    roumuhiBackgroundColor: '#FFD4A3',         // Light orange background for roumuhi section
    
    // Default typography settings
    fontSize: '10px',                          // Standard font size
    fontFamily: 'Yu Gothic',                   // Japanese font family
    
    // Default display options - all enabled
    showCompanyName: true,                     // Show company name
    showEmployeeCode: true,                    // Show employee code
    showMemo: true,                            // Show memo section
    
    // Default field settings - all items enabled by default
    furikomi: {
      enabled: constructionFurikomiItems.map(item => item.id),  // All furikomi items enabled
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin'
    },
    payment: {
      enabled: constructionPaymentItems.map(item => item.id),   // All payment items enabled
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin'
    },
    deduction: {
      // First 6 deduction items enabled by default (standard deductions)
      enabled: constructionDeductionItems.slice(0, 6).map(item => item.id),
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin'
    },
    attendance: {
      // All attendance items enabled by default (includes 時間外, 深夜, 休日)
      enabled: constructionAttendanceItems.map(item => item.id),
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin'
    },
    notes: {
      enabled: constructionNotesItems.map(item => item.id),     // All notes items enabled (empty by default)
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin'
    },
    sonota: {
      enabled: constructionSonotaItems.map(item => item.id),    // All その他 items enabled by default
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin'
    }
  };
};

/**
 * Saves payslip settings to API with localStorage fallback.
 * 
 * Converts legacy format to API format and attempts to save to backend.
 * Falls back to localStorage if API save fails.
 * 
 * @param settings - ConstructionPayslipSettings to save
 * @param dataType - Type of settings to save ('construction')
 * @returns Promise resolving to boolean indicating success
 * 
 * @example
 * ```ts
 * const success = await saveSettings(settings, 'construction');
 * if (success) {
 *   console.log('Settings saved successfully');
 * }
 * ```
 */
export const saveSettings = async (
  settings: ConstructionPayslipSettings,
  dataType: ConstructionSettingsDataType
): Promise<boolean> => {
  if (dataType === CONSTRUCTION_BONUS_SETTINGS_DATA_TYPE) {
    saveSettingsToLocalStorage(settings, CONSTRUCTION_BONUS_SETTINGS_DATA_TYPE);
    return true;
  }

  try {
    const apiSettings = convertLegacySettingsToApi(settings, 'construction');
    const success = await PayslipSettingsService.saveSettingsWithFallback('construction', apiSettings);
    if (!success) {
      console.error(`Failed to save settings for ${dataType}`);
    }
    return success;
  } catch (error) {
    console.error(`Error saving settings for ${dataType}:`, error);
    return false;
  }
};

/**
 * Converts legacy settings format to API format.
 * 
 * Maps ConstructionPayslipSettings to ApiPayslipSettings format for backend API.
 * 
 * @param legacySettings - Legacy format settings
 * @param dataType - Type of settings ('construction')
 * @returns API format PayslipSettings
 * 
 * @private
 */
const convertLegacySettingsToApi = (legacySettings: ConstructionPayslipSettings, dataType: 'construction'): ApiPayslipSettings => {
  return {
    dataType,
    // Appearance settings
    borderColor: legacySettings.borderColor,
    backgroundColor: legacySettings.backgroundColor,
    roumuhiBorderColor: legacySettings.roumuhiBorderColor,
    roumuhiBackgroundColor: legacySettings.roumuhiBackgroundColor,
    
    // Typography settings
    fontSize: legacySettings.fontSize,
    fontFamily: legacySettings.fontFamily,
    
    // Display toggles
    showCompanyName: legacySettings.showCompanyName,
    showEmployeeCode: legacySettings.showEmployeeCode,
    showMemo: legacySettings.showMemo,
    
    // Field settings (all sections; sonota is frontend-only, not persisted to API)
    payment: legacySettings.payment,
    deduction: legacySettings.deduction,
    attendance: legacySettings.attendance,
    notes: legacySettings.notes,
    furikomi: legacySettings.furikomi
  };
};

/**
 * Updates specific field settings for a particular field type.
 * 
 * Allows updating individual field sections (payment, deduction, attendance, etc.)
 * without updating the entire settings object.
 * 
 * @param dataType - Type of settings ('construction')
 * @param fieldType - Type of field to update ('payment', 'deduction', 'attendance', 'notes', 'furikomi')
 * @param fieldSettings - New field settings to apply
 * @returns Promise resolving to boolean indicating success
 * 
 * @example
 * ```ts
 * const success = await updateFieldSettings(
 *   'construction',
 *   'payment',
 *   { enabled: ['cp_k1', 'cp_k2'], lastUpdated: '...', updatedBy: 'admin' }
 * );
 * ```
 */
export const updateFieldSettings = async (
  dataType: 'construction',
  fieldType: 'payment' | 'deduction' | 'attendance' | 'notes' | 'furikomi',
  fieldSettings: FieldSettings
): Promise<boolean> => {
  try {
    const success = await PayslipSettingsService.updateFieldSettings(dataType, fieldType, fieldSettings);
    
    if (!success) {
      console.error(`Failed to update ${fieldType} settings for ${dataType}`);
    }
    return success;
  } catch (error) {
    console.error(`Error updating ${fieldType} settings for ${dataType}:`, error);
    return false;
  }
};

/**
 * Legacy localStorage functions (kept for backward compatibility during migration).
 * 
 * These functions are maintained for backward compatibility but are generally
 * not used directly. The loadSettings and saveSettings functions handle API
 * calls with automatic localStorage fallback.
 * 
 * @deprecated Use loadSettings() and saveSettings() instead, which handle API
 *             calls with localStorage fallback automatically.
 */

/**
 * Loads settings from localStorage (legacy function).
 * 
 * Reads settings from browser localStorage and parses them.
 * Returns default settings if parsing fails or no settings are found.
 * 
 * @param dataType - Type of settings to load ('construction')
 * @returns ConstructionPayslipSettings object
 * 
 * @deprecated Use loadSettings() instead which includes API integration
 */
export const loadSettingsFromLocalStorage = (dataType: ConstructionSettingsDataType): ConstructionPayslipSettings => {
  const storageKey = `payslipSettings_${dataType}`;
  const saved = localStorage.getItem(storageKey);
  const bonusDefaults = dataType === CONSTRUCTION_BONUS_SETTINGS_DATA_TYPE;

  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      const defaultSettings = bonusDefaults ? getDefaultBonusSettings() : getDefaultSettings();
      return {
        borderColor: parsed.borderColor || '#4caf50',
        backgroundColor: parsed.backgroundColor || '#eaffea',
        roumuhiBorderColor: parsed.roumuhiBorderColor || '#FF8C00',
        roumuhiBackgroundColor: parsed.roumuhiBackgroundColor || '#FFD4A3',
        fontSize: parsed.fontSize || '10px',
        fontFamily: parsed.fontFamily || 'Yu Gothic',
        showCompanyName: parsed.showCompanyName !== undefined ? parsed.showCompanyName : true,
        showEmployeeCode: parsed.showEmployeeCode !== undefined ? parsed.showEmployeeCode : true,
        showMemo: parsed.showMemo !== undefined ? parsed.showMemo : true,
        furikomi: {
          enabled: parsed.furikomi?.enabled || defaultSettings.furikomi.enabled,
          lastUpdated: parsed.furikomi?.lastUpdated || '',
          updatedBy: parsed.furikomi?.updatedBy || ''
        },
        payment: {
          enabled: mergeEnabledFieldIds(parsed.payment?.enabled, defaultSettings.payment.enabled),
          lastUpdated: parsed.payment?.lastUpdated || '',
          updatedBy: parsed.payment?.updatedBy || ''
        },
        deduction: {
          enabled: mergeEnabledFieldIds(parsed.deduction?.enabled, defaultSettings.deduction.enabled),
          lastUpdated: parsed.deduction?.lastUpdated || '',
          updatedBy: parsed.deduction?.updatedBy || ''
        },
        attendance: {
          enabled: bonusDefaults
            ? []
            : mergeEnabledFieldIds(
                parsed.attendance?.enabled,
                constructionAttendanceItems.map(item => item.id)
              ),
          lastUpdated: parsed.attendance?.lastUpdated || '',
          updatedBy: parsed.attendance?.updatedBy || ''
        },
        notes: {
          enabled: mergeEnabledFieldIds(parsed.notes?.enabled, defaultSettings.notes.enabled),
          lastUpdated: parsed.notes?.lastUpdated || '',
          updatedBy: parsed.notes?.updatedBy || ''
        },
        sonota: {
          enabled: mergeEnabledFieldIds(parsed.sonota?.enabled, defaultSettings.sonota.enabled),
          lastUpdated: parsed.sonota?.lastUpdated || '',
          updatedBy: parsed.sonota?.updatedBy || ''
        }
      };
    } catch (error) {
      console.error('Error parsing saved settings:', error);
    }
  }

  return bonusDefaults ? getDefaultBonusSettings() : getDefaultSettings();
};

/**
 * Saves settings to localStorage (legacy function).
 * 
 * Stores settings in browser localStorage as JSON string.
 * 
 * @param settings - ConstructionPayslipSettings to save
 * @param dataType - Type of settings ('construction')
 * 
 * @deprecated Use saveSettings() instead which includes API integration
 */
export const saveSettingsToLocalStorage = (
  settings: ConstructionPayslipSettings,
  dataType: ConstructionSettingsDataType
): void => {
  const storageKey = `payslipSettings_${dataType}`;
  localStorage.setItem(storageKey, JSON.stringify(settings));
};